import { useMemo, useState } from "react";
import type {
  Link,
  SocialLinks,
  UpdateProfileInput,
  User,
} from "@dropin/shared/schemas";
import { socialPlatforms, verificationStateOf } from "@dropin/shared/schemas";
import {
  adminUpdateUserProfile,
  requestVerification,
  updateMyProfile,
} from "../api";
import { AvatarUploader } from "./AvatarUploader";
import { CityAutocomplete } from "./CityAutocomplete";
import { LinksEditor } from "./LinksEditor";

const SOCIAL_META: Record<
  (typeof socialPlatforms)[number],
  { label: string; placeholder: string }
> = {
  instagram: { label: "Instagram", placeholder: "@yourname" },
  tiktok: { label: "TikTok", placeholder: "@yourname" },
  facebook: { label: "Facebook", placeholder: "facebook.com/yourname" },
  youtube: { label: "YouTube", placeholder: "youtube.com/@yourname" },
  twitter: { label: "X / Twitter", placeholder: "@yourname" },
};

type Draft = {
  displayName: string;
  avatarUrl: string;
  bio: string;
  city: string;
  neighborhood: string;
  socialLinks: SocialLinks;
  links: Link[];
};

function toDraft(u: User): Draft {
  return {
    displayName: u.displayName ?? "",
    avatarUrl: u.avatarUrl ?? "",
    bio: u.bio ?? "",
    city: u.city ?? "",
    neighborhood: u.neighborhood ?? "",
    socialLinks: u.socialLinks ?? {},
    links: u.links ?? [],
  };
}

function diffPayload(draft: Draft, base: Draft): UpdateProfileInput {
  const out: UpdateProfileInput = {};
  const blankToNull = (v: string) => (v.trim() === "" ? null : v.trim());

  if (draft.displayName !== base.displayName)
    out.displayName = blankToNull(draft.displayName);
  if (draft.avatarUrl !== base.avatarUrl) out.avatarUrl = blankToNull(draft.avatarUrl);
  if (draft.bio !== base.bio) out.bio = blankToNull(draft.bio);
  if (draft.city !== base.city) out.city = blankToNull(draft.city);
  if (draft.neighborhood !== base.neighborhood)
    out.neighborhood = blankToNull(draft.neighborhood);

  const trimmedSocial: SocialLinks = {};
  for (const k of socialPlatforms) {
    const v = draft.socialLinks[k]?.trim();
    if (v) trimmedSocial[k] = v;
  }
  if (JSON.stringify(trimmedSocial) !== JSON.stringify(base.socialLinks)) {
    out.socialLinks = Object.keys(trimmedSocial).length === 0 ? null : trimmedSocial;
  }

  // Drop rows where either field is empty; persist null when the list is empty.
  const trimmedLinks: Link[] = draft.links
    .map((l) => ({ label: l.label.trim(), url: l.url.trim() }))
    .filter((l) => l.label && l.url);
  if (JSON.stringify(trimmedLinks) !== JSON.stringify(base.links)) {
    out.links = trimmedLinks.length === 0 ? null : trimmedLinks;
  }
  return out;
}

export function CreatorProfileTab({
  user,
  onUpdated,
  mode = "self",
}: {
  user: User;
  onUpdated: (u: User) => void;
  // "self": creator editing their own profile (PATCH /me; shows verification banner)
  // "admin": admin editing someone else's (PATCH /admin/users/:id/profile; banner hidden)
  mode?: "self" | "admin";
}) {
  const base = useMemo(() => toDraft(user), [user]);
  const [draft, setDraft] = useState<Draft>(base);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const dirty = useMemo(() => Object.keys(diffPayload(draft, base)).length > 0, [draft, base]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
    setSavedAt(null);
  }
  function setSocial(platform: (typeof socialPlatforms)[number], value: string) {
    setDraft((d) => ({ ...d, socialLinks: { ...d.socialLinks, [platform]: value } }));
    setSavedAt(null);
  }

  async function save() {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      const payload = diffPayload(draft, base);
      const { user: updated } =
        mode === "admin"
          ? await adminUpdateUserProfile(user.id, payload)
          : await updateMyProfile(payload);
      onUpdated(updated);
      setSavedAt(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-5 pb-28 md:pb-0">
      {/* Verification banner only shows for the user themselves —
          admins get a separate decision bar on the detail page. */}
      {mode === "self" && <VerificationBanner user={user} onUpdated={onUpdated} />}

      {/* Basics */}
      <Section title="Who you are" subtitle="What businesses see when you claim a perk.">
        <AvatarUploader
          value={draft.avatarUrl || null}
          onChange={(v) => set("avatarUrl", v ?? "")}
          kind="avatars"
          scopeId={user.id}
          fallback={draft.displayName || user.email}
          shape="circle"
          label="Profile photo"
          hint="JPG / PNG / WebP / GIF, up to 5 MB."
          testid="profile-avatar"
        />
        <Field label="Display name" required>
          <input
            className="input"
            value={draft.displayName}
            onChange={(e) => set("displayName", e.target.value)}
            placeholder="Avery Chen"
            maxLength={80}
            data-testid="profile-display-name"
          />
        </Field>
        <Field label="Bio" hint="One or two lines. What do you make, and for whom?">
          <textarea
            className="input min-h-[88px]"
            value={draft.bio}
            onChange={(e) => set("bio", e.target.value)}
            placeholder="SF food + coffee shorts. I post the spots my neighbors actually walk to."
            maxLength={500}
            data-testid="profile-bio"
          />
        </Field>
      </Section>

      {/* Location */}
      <Section
        title="Your home base"
        subtitle="Tell us where you live. When you're out and about, DropIn uses your device location to surface perks near you — so you'll find drops wherever you go, not just in your home region."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="City">
            <CityAutocomplete
              value={draft.city}
              onChange={(v) => set("city", v)}
              placeholder="Start typing a city…"
              testid="profile-city"
            />
          </Field>
          <Field label="Neighborhood" optional>
            <input
              className="input"
              value={draft.neighborhood}
              onChange={(e) => set("neighborhood", e.target.value)}
              placeholder="Mission, Inner Sunset, Bernal Heights…"
              data-testid="profile-neighborhood"
            />
          </Field>
        </div>
      </Section>

      {/* Platforms */}
      <Section
        title="Your platforms"
        subtitle="Where your audience lives. Used during verification and shown to businesses."
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {socialPlatforms.map((p) => (
            <Field key={p} label={SOCIAL_META[p].label}>
              <input
                className="input"
                value={draft.socialLinks[p] ?? ""}
                onChange={(e) => setSocial(p, e.target.value)}
                placeholder={SOCIAL_META[p].placeholder}
                data-testid={`profile-social-${p}`}
              />
            </Field>
          ))}
        </div>
      </Section>

      {/* More links — free-form, anything that isn't one of the 5 socials */}
      <Section
        title="More links"
        subtitle="Personal site, Substack, Patreon, Linktree — anywhere else your work lives."
      >
        <LinksEditor
          value={draft.links}
          onChange={(links) => set("links", links)}
          testid="profile-links"
        />
      </Section>

      <SaveBar
        dirty={dirty}
        saving={saving}
        savedAt={savedAt}
        error={error}
        onSave={save}
        onDiscard={() => {
          setDraft(base);
          setError(null);
          setSavedAt(null);
        }}
      />
    </div>
  );
}

function VerificationBanner({
  user,
  onUpdated,
}: {
  user: User;
  onUpdated: (u: User) => void;
}) {
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const state = verificationStateOf(user);

  if (state === "verified") {
    return (
      <div
        className="rounded-lg border border-success/40 bg-success/[0.05] p-3.5 flex items-center gap-3"
        data-testid="verification-banner"
        data-state="verified"
      >
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">Verified creator</p>
          <p className="text-xs text-muted">Businesses see the verified badge next to your claims.</p>
        </div>
      </div>
    );
  }

  if (state === "pending") {
    return (
      <div
        className="rounded-lg border border-accent/40 bg-accent/[0.07] p-3.5 flex items-center gap-3"
        data-testid="verification-banner"
        data-state="pending"
      >
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/20 text-accent">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">Pending review</p>
          <p className="text-xs text-muted">
            An admin is reviewing your profile. You'll see the verified badge here once approved.
          </p>
        </div>
      </div>
    );
  }

  // Unverified — show CTA
  async function submit() {
    setRequesting(true);
    setError(null);
    try {
      const { user: updated } = await requestVerification();
      onUpdated(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed");
    } finally {
      setRequesting(false);
    }
  }

  return (
    <div
      className="rounded-lg border border-border bg-paper p-4 flex items-start gap-3 flex-wrap"
      data-testid="verification-banner"
      data-state="unverified"
    >
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-paper text-muted border border-border">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">Unverified</p>
        <p className="mt-0.5 text-xs text-muted leading-relaxed">
          Fill in your profile and platforms first, then request verification. A
          DropIn admin will review and confirm you fit what we look for in a
          creator.
        </p>
        {error && <p className="mt-2 text-xs text-rose">{error}</p>}
      </div>
      <button
        type="button"
        className="btn-primary text-sm shrink-0"
        onClick={submit}
        disabled={requesting}
        data-testid="request-verification"
      >
        {requesting ? "Requesting…" : "Request verification"}
      </button>
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card">
      <header>
        <h3 className="text-lg font-semibold tracking-tightish">{title}</h3>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </header>
      <div className="mt-5 grid gap-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  required,
  optional,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted">
        {label}
        {required && <span className="text-rose ml-1">*</span>}
        {optional && (
          <span className="ml-1 font-normal normal-case tracking-normal text-muted/70">
            (optional)
          </span>
        )}
      </span>
      {children}
      {hint && <span className="text-xs text-muted">{hint}</span>}
    </label>
  );
}

function SaveBar({
  dirty,
  saving,
  savedAt,
  error,
  onSave,
  onDiscard,
}: {
  dirty: boolean;
  saving: boolean;
  savedAt: number | null;
  error: string | null;
  onSave: () => void;
  onDiscard: () => void;
}) {
  const justSaved = savedAt !== null && Date.now() - savedAt < 4000;
  return (
    <>
      <div
        className={`md:hidden fixed inset-x-0 bottom-0 z-20 border-t border-border bg-surface/95 backdrop-blur px-4 py-3 transition-transform ${
          dirty || saving || justSaved || error ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted truncate">
            {error
              ? <span className="text-rose">{error}</span>
              : saving
                ? "Saving…"
                : justSaved
                  ? "Saved ✓"
                  : dirty
                    ? "Unsaved changes"
                    : ""}
          </span>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              className="btn text-sm"
              onClick={onDiscard}
              disabled={!dirty || saving}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={onSave}
              disabled={!dirty || saving}
              data-testid="profile-save"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>

      <div className="hidden md:flex items-center justify-end gap-3">
        <span className="text-sm text-muted">
          {error
            ? <span className="text-rose">{error}</span>
            : saving
              ? "Saving…"
              : justSaved
                ? "Saved ✓"
                : dirty
                  ? "Unsaved changes"
                  : ""}
        </span>
        <button
          type="button"
          className="btn"
          onClick={onDiscard}
          disabled={!dirty || saving}
        >
          Discard
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onSave}
          disabled={!dirty || saving}
          data-testid="profile-save-desktop"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </>
  );
}
